import {noop} from 'lodash';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {Plural} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {AsyncStatusesEnum} from '../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {InboxModel} from '../../../../../libs/shared/core/src/models/api/inboxModel';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {AsyncLoader} from '../../../../../libs/shared/ui/src/components/loaders/asyncLoader';
import {ColorDot} from '../../../../../libs/shared/ui/src/components/utility/colorDot';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {inboxHasColor} from '../../../../helpers/modelHelpers';

/*
 * Props.
 */

interface InboxDropdownValueProps {
  inbox: InboxModel | undefined;
  status?: AsyncStatusesEnum;
  refetch?: () => void;
  isMultiSelect?: boolean;
}

/*
 * Styles.
 */

const emptyValueStyles = css`
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledValueDiv = styled.div<{$isEmpty?: boolean}>`
  display: grid;
  align-items: center;
  grid-auto-flow: column;
  grid-gap: 12px;
  max-width: 160px;

  ${(p) => (p.$isEmpty ? emptyValueStyles : '')}
`;

const StyledValueNameDiv = styled.div`
  ${ellipsis()}
`;

/*
 * Component.
 */

export const InboxDropdownValue: FC<InboxDropdownValueProps> = (props) => {
  const {inbox, status, refetch = noop, isMultiSelect} = props;

  const theme = useUserTheme();

  if (status && status !== AsyncStatusesEnum.LOADED) {
    return <AsyncLoader status={status} onRetryClick={refetch} size={VisualSizesEnum.SMALL} />;
  }
  if (!inbox) {
    return (
      <StyledValueDiv $isEmpty>
        {!isMultiSelect && <Icon name="inbox" />}
        <Plural
          id="inbox-dropdown-value-empty"
          one="Select inbox"
          other="Select inboxes"
          value={isMultiSelect ? 2 : 1}
        />
      </StyledValueDiv>
    );
  }

  return (
    <StyledValueDiv>
      <ColorDot size={8} color={inboxHasColor(inbox) ? inbox.color : theme.greys.shade40} />
      <StyledValueNameDiv>{inbox.name}</StyledValueNameDiv>
    </StyledValueDiv>
  );
};
